import { MASAR } from "@/constants/testIds";
import { SectionLabel } from "@/components/masar/HowItWorks";
import { PLACES } from "@/components/masar/media";
import { MapPin } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { AR_CITIES } from "@/i18n/translations";

// Country order must match coverage.regions arrays in translations.js
const REGIONS_META = [
  { tag: "hq", cities: ["Cairo", "Alexandria", "Giza", "Ain Sokhna", "Hurghada", "El Gouna", "Sharm El Sheikh"] },
  { tag: "new", cities: ["Casablanca", "Marrakech", "Rabat", "Tangier", "Agadir"] },
  { tag: "new", cities: ["Algiers", "Oran", "Constantine", "Annaba"] },
  { tag: "new", cities: ["Tunis", "Sfax", "Sousse", "Djerba"] },
  { tag: "new", cities: ["Tripoli", "Benghazi", "Misrata"] },
  { tag: "new", cities: ["Amman", "Aqaba", "Petra", "Irbid"] },
  { tag: "new", cities: ["Beirut", "Byblos", "Tripoli", "Zahle"] },
  { tag: "new", cities: ["Istanbul", "Ankara", "Izmir", "Antalya", "Cappadocia", "Bodrum"] },
  { tag: "live", cities: ["Riyadh", "Jeddah", "Dammam", "Al Khobar", "Mecca", "Medina", "AlUla"] },
  { tag: "live", cities: ["Dubai", "Abu Dhabi", "Sharjah", "Al Ain", "Ras Al Khaimah", "Fujairah"] },
  { tag: "live", cities: ["Kuwait City", "Hawalli", "Ahmadi", "Jahra"] },
  { tag: "live", cities: ["Doha", "Al Rayyan", "Al Wakrah", "Al Khor"] },
  { tag: "live", cities: ["Manama", "Riffa", "Muharraq"] },
  { tag: "live", cities: ["Muscat", "Salalah", "Nizwa", "Sohar"] },
];

export default function Coverage() {
  const { lang, t } = useLanguage();
  const names = t("coverage.regions");
  const tagLabels = t("coverage.tags");
  const regions = REGIONS_META.map((r, i) => ({
    ...r,
    title: names[i],
    tagLabel: tagLabels[r.tag],
    cities: lang === "ar" ? AR_CITIES[i] : r.cities,
  }));

  return (
    <section
      id="coverage"
      data-testid={MASAR.sectionCoverage}
      className="relative py-24 sm:py-32 border-t border-white/10"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionLabel index="04" label={t("coverage.chapter")} />

        <div className="mt-6 grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
              {t("coverage.titleA")}
              <br />
              <span className="text-[#FF6A1A]">{t("coverage.titleB")}</span>
            </h2>
            <p className="mt-6 text-neutral-400 max-w-md leading-relaxed">
              {t("coverage.para")}
            </p>

            <div className="mt-8 grid grid-cols-2 gap-3">
              <figure className="relative h-32 rounded-2xl overflow-hidden border border-white/10 group">
                <img
                  src={PLACES.desertDunes}
                  alt="Western Desert dunes at golden hour"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <figcaption className="absolute bottom-2 left-3 text-[10px] font-mono uppercase tracking-widest text-white">
                  {t("coverage.cap1")}
                </figcaption>
              </figure>
              <figure className="relative h-32 rounded-2xl overflow-hidden border border-white/10 group">
                <img
                  src={PLACES.gulfCoast}
                  alt="Gulf coastline"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <figcaption className="absolute bottom-2 left-3 text-[10px] font-mono uppercase tracking-widest text-white">
                  {t("coverage.cap2")}
                </figcaption>
              </figure>
            </div>

            <div className="mt-4 rounded-2xl border border-white/10 p-5 bg-neutral-950">
              <div className="flex items-center gap-3">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#FF6A1A] text-white">
                  <MapPin size={18} />
                </span>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">
                    {t("coverage.region")}
                  </div>
                  <div className="font-semibold">{t("coverage.regionValue")}</div>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-3 text-center">
                <Bit v="14" k={t("coverage.bitCountries")} />
                <Bit v="60+" k={t("coverage.bitCities")} />
                <Bit v="24/7" k={t("coverage.bitSos")} />
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="rounded-3xl border border-white/10 overflow-hidden bg-neutral-900/40 p-4 sm:p-6">
              <div className="space-y-3">
                {regions.map((r) => (
                  <div
                    key={r.title}
                    className="group rounded-2xl border border-white/10 bg-neutral-950 p-4 sm:p-5 hover:border-[#FF6A1A] transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="h-2 w-2 rounded-full bg-[#FF6A1A]" />
                        <span className="text-base font-semibold">{r.title}</span>
                      </div>
                      <span
                        className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full border ${
                          r.tag === "hq"
                            ? "border-white/20 text-white bg-white/10"
                            : r.tag === "new"
                              ? "border-transparent text-white bg-[#FF6A1A] shadow-[0_0_20px_rgba(255,106,26,0.5)]"
                              : "border-[#FF6A1A]/30 text-[#FF6A1A]"
                        }`}
                      >
                        {r.tagLabel}
                      </span>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {r.cities.map((c) => (
                        <span
                          key={c}
                          className="text-xs px-2.5 py-1 rounded-full bg-white/10 text-neutral-300 border border-white/10"
                        >
                          {c}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Bit({ v, k }) {
  return (
    <div className="border-l border-white/10 pl-3 text-left">
      <div className="text-lg font-semibold">{v}</div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">{k}</div>
    </div>
  );
}
